import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Configuration d'axios pour les exports
const exportApi = axios.create({
  baseURL: API_BASE_URL,
  responseType: 'blob', // Important pour les fichiers binaires
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
exportApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs
exportApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Fonction utilitaire pour télécharger un fichier
const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Services d'export
export const exportService = {
  // Export des produits en Excel
  exportProductsToExcel: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (key === 'selectedIds' && Array.isArray(filters.selectedIds) && filters.selectedIds.length > 0) {
          params.append('ids', filters.selectedIds.join(','));
        } else if (filters[key] !== undefined && filters[key] !== '' && key !== 'selectedIds') {
          params.append(key, filters[key]);
        }
      });

      const response = await exportApi.get(`/export/products/excel?${params.toString()}`);
      
      const filename = `produits_domoelec_${new Date().toISOString().split('T')[0]}.xlsx`;
      downloadFile(response.data, filename);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de l\'export Excel des produits:', error);
      return { success: false, error: 'Erreur lors de l\'export Excel des produits' };
    }
  },

  // Export des produits en PDF
  exportProductsToPDF: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (key === 'selectedIds' && Array.isArray(filters.selectedIds) && filters.selectedIds.length > 0) {
          params.append('ids', filters.selectedIds.join(','));
        } else if (filters[key] !== undefined && filters[key] !== '' && key !== 'selectedIds') {
          params.append(key, filters[key]);
        }
      });

      const response = await exportApi.get(`/export/products/pdf?${params.toString()}`);
      
      const filename = `produits_domoelec_${new Date().toISOString().split('T')[0]}.pdf`;
      downloadFile(response.data, filename);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de l\'export PDF des produits:', error);
      return { success: false, error: 'Erreur lors de l\'export PDF des produits' };
    }
  },

  // Export des catégories en Excel
  exportCategoriesToExcel: async (includeInactive = false) => {
    try {
      const params = new URLSearchParams();
      if (includeInactive) {
        params.append('includeInactive', 'true');
      }

      const response = await exportApi.get(`/export/categories/excel?${params.toString()}`);
      
      const filename = `categories_domoelec_${new Date().toISOString().split('T')[0]}.xlsx`;
      downloadFile(response.data, filename);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de l\'export Excel des catégories:', error);
      return { success: false, error: 'Erreur lors de l\'export Excel des catégories' };
    }
  },

  // Export d'un produit spécifique en Excel
  exportSingleProductToExcel: async (productId) => {
    try {
      const response = await exportApi.get(`/export/products/${productId}/excel`);
      
      const filename = `produit_${productId}_${new Date().toISOString().split('T')[0]}.xlsx`;
      downloadFile(response.data, filename);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de l\'export Excel du produit:', error);
      return { success: false, error: 'Erreur lors de l\'export Excel du produit' };
    }
  },

  // Export d'un produit spécifique en PDF
  exportSingleProductToPDF: async (productId) => {
    try {
      const response = await exportApi.get(`/export/products/${productId}/pdf`);
      
      const filename = `produit_${productId}_${new Date().toISOString().split('T')[0]}.pdf`;
      downloadFile(response.data, filename);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de l\'export PDF du produit:', error);
      return { success: false, error: 'Erreur lors de l\'export PDF du produit' };
    }
  },

  // Export des demandes de devis en Excel
  exportQuotesToExcel: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== '') {
          params.append(key, filters[key]);
        }
      });

      const response = await exportApi.get(`/export/quotes/excel?${params.toString()}`);
      
      const filename = `demandes_devis_domoelec_${new Date().toISOString().split('T')[0]}.xlsx`;
      downloadFile(response.data, filename);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de l\'export Excel des demandes de devis:', error);
      return { success: false, error: 'Erreur lors de l\'export Excel des demandes de devis' };
    }
  },

  // Export des statistiques en PDF
  exportStatsToPDF: async () => {
    try {
      const response = await exportApi.get('/export/stats/pdf');
      
      const filename = `statistiques_domoelec_${new Date().toISOString().split('T')[0]}.pdf`;
      downloadFile(response.data, filename);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de l\'export PDF des statistiques:', error);
      return { success: false, error: 'Erreur lors de l\'export PDF des statistiques' };
    }
  },

  // Télécharger template Excel pour l'import
  downloadImportTemplate: async () => {
    try {
      const response = await exportApi.get('/export/products/template');
      
      const filename = `template_import_produits_${new Date().toISOString().split('T')[0]}.xlsx`;
      downloadFile(response.data, filename);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors du téléchargement du template:', error);
      return { success: false, error: 'Erreur lors du téléchargement du template' };
    }
  }
};

export default exportService;
